import {makeAutoObservable, runInAction} from "mobx";
import {getStore, useStore} from "../index";
import {COMPONENTS_MAP, MENU_PATHS_MAP} from "@/components/constants/routes";
import {setFilterActiveMenuList, setMakePagePath, setTreeStructure} from "@/utils/menu";


/**
 * 홈페이지 네비게이션 및 메뉴 상태 관리 Store
 */
export class NavigationStore {

    /**
     * 전체 네비게이션 메뉴 리스트
     * @type {Array<Object>}
     */
    navigateList = [];

    /**
     * 헤더 활성화 여부
     * @type {boolean}
     */
    headActive = false;

    /**
     * 현재 오픈된 메뉴 인덱스
     * @type {string|Object|null}
     */
    openIndex = null;

    /**
     * 모바일에서 열려있는 메뉴 인덱스 리스트
     * @type {Array<Object>}
     */
    mobileOpenIdx = [];

    /**
     * 사이드 네비게이션 오픈 여부
     * @type {boolean}
     */

    sideNavigationOpen = false;

    /**
     * 모바일 메뉴 초기 오픈 여부 체크값
     * @type {boolean}
     */
    didInitMobileOpen = false;

    /**
     * 페이지 메뉴 요소 리스트
     * @type {Array<Object>}
     */
    pageMenuElements = [];

    /**
     * NavigationStore 생성자
     *
     * @param {Object} store 루트 스토어 객체
     * @param {Object} initialNavigation 초기 네비게이션 데이터
     */
    constructor(store, initialNavigation) {
        this.store = store;
        makeAutoObservable(this, {}, {autoBind: true});
        if (initialNavigation) {
            if(initialNavigation && initialNavigation.result){
                this.navigateList = setFilterActiveMenuList(initialNavigation.data.menus);
            }
        }
    }

    /**
     * 헤더 활성화 상태 변경 함수
     *
     * @param {boolean} value 활성화 여부
     */
    setHeadActive(value) {
        this.headActive = value;
    }

    /**
     * 현재 오픈된 메뉴 인덱스 변경 함수
     *
     * @param {number|null} idx 메뉴 인덱스
     */
    setOpenIndex(idx) {
        this.openIndex = idx;
    }

    /**
     * 모바일 메뉴 토글 함수
     *
     * @param {number} idx 메뉴 인덱스
     * @param {boolean} clear 기존 메뉴 초기화 여부
     */
    setToggleMobile(idx, clear) {
        if(clear){
            this.mobileOpenIdx = [idx];
        }else {
            if (this.mobileOpenIdx.includes(idx)) {
                this.mobileOpenIdx = this.mobileOpenIdx.filter(i => i !== idx);
            } else {
                this.mobileOpenIdx.push(idx);
            }
        }
    }

    /**
     * 사이드 네비게이션 오픈 상태 변경 함수
     *
     * @param {boolean} value 오픈 여부
     */
    setSideNavigationOpen(value) {
        this.sideNavigationOpen = value;
    }

    /**
     * 활성 메뉴 기준 모바일 메뉴 자동 오픈 처리 함수
     *
     * @param {Object} node 메뉴 노드 데이터
     * @param {number} idx 메뉴 인덱스
     */
    setMenuOpen(node, idx){
        if(node.active){
            if (!this.didInitMobileOpen) {
                this.mobileOpenIdx.push(idx);
                this.didInitMobileOpen = true;
            }
        }
    }

    /**
     * 특정 메뉴 기준 트리 구조 생성 함수
     *
     * @param {string} menuId 메뉴 ID
     * @returns {Array}
     */
    setMakeNaviMenu(menuId) {
        return setTreeStructure(menuId, this.navigateList);
    }

    /**
     * 트리 구조 내 첫 번째 FOLDER가 아닌 메뉴 탐색 함수
     *
     * @param {Array} nodes 메뉴 노드 리스트
     * @returns {Object|null}
     */
    findFirstNonFolder(nodes) {
        if (!nodes || nodes.length === 0) return null;
        for (const node of nodes) {
            if (node && node.type !== "FOLDER") {
                return node;
            }
        }
        const nextLevel = [];
        for (const node of nodes) {
            if (node && node.type === "FOLDER" && node.children && node.children.length > 0) {
                nextLevel.push(...node.children);
            }
        }
        if (nextLevel.length === 0) return null;
        return this.findFirstNonFolder(nextLevel);
    }

    /**
     * 현재 메뉴 기준 페이지 메뉴 리스트 생성 함수
     *
     * @param {string} menuId 메뉴 ID
     * @returns {Array|undefined}
     */
    setMakePageMenu(menuId) {
        if(!menuId) return;
        const data = setTreeStructure(menuId, this.navigateList);
        const activeItem = data.find(item => item.menuId === menuId);
        const parent2Id = activeItem?.trail?.[1]?.menuId ?? null;
        const depth3List = data.filter(node => node.depth === 3 && node.trail[1]?.menuId === parent2Id);
        return depth3List.map(depth3Item => {
            if (depth3Item.type !== "FOLDER") {
                return depth3Item;
            }
            const depth4List = data.filter(node => node.depth === 4 && node.trail[2]?.menuId === depth3Item.menuId );
            const options = [
                ...depth4List.map(d4 => ({
                    value: d4.menuId,
                    label: d4.title,
                })),
            ];
            const active4Id = depth4List.find(it => it.active)?.title ?? "";
            const isInThisFolder = activeItem?.depth === 4 && activeItem?.trail?.[2]?.menuId === depth3Item.menuId;
            const value = isInThisFolder ? active4Id : "";
            return {
                selectMenu: {
                    data: depth4List,
                    title: depth3Item.title,
                    option: options,
                    value: value,
                    active: depth3Item.active,
                }
            };
        });
    }

    /**
     * 메뉴 타입에 따라 페이지 URL 생성 함수
     *
     * @param {Object} target 대상 메뉴 데이터
     * @param {string|undefined} targetPath 강제 지정 경로
     * @param {string|undefined} menuId 현재 메뉴 ID
     * @returns {string|boolean}
     */
    setFilterPageUrl(target, targetPath = undefined, menuId = undefined){
        const activeItem = setTreeStructure(menuId, this.navigateList).find(item => item.menuId === menuId);
        const rootTitle = activeItem?.trail?.[0]?.title ?? null;
        let basePath = MENU_PATHS_MAP.find(map => map.name === rootTitle)?.path;
        if(targetPath){
            basePath = targetPath;
        }
        if(target.type === "FOLDER"){
            alert("준비 중인 폴더 입니다.");
        } else if(target.type === "BOARD"){
           return setMakePagePath(target.menuId, undefined, target.type.toLowerCase(), target.subType.toLowerCase());
        } else if(target.type === "TEMPLATE" || target.type === "FORM"){
            return setMakePagePath(target.menuId, undefined, target.type.toLowerCase(), undefined);
        } else if(target.type === "E-NEWS"){
            return setMakePagePath(target.menuId, undefined, "news", undefined);
        } else if(target.type === "PAGE"){
            if (target && target.component) {
                const componentsPath = COMPONENTS_MAP.find(map => map.name === target.component)?.path;
                return setMakePagePath(target.menuId,componentsPath,  basePath );
            }
        }
        return  false;
    }


    /**
     * 페이지 메뉴 요소 초기화 함수
     */
    resetPageMenu() {
        this.pageMenuElements = [];
    }

    /**
     * 네비게이션 상태 초기화 함수
     *
     * @param {string|undefined} type 초기화 타입
     */
    setClearNavigation(type = undefined) {
        this.openIndex = null;
        this.mobileOpenIdx = [];
        this.didInitMobileOpen = false;
        this.sideNavigationOpen = false;
    }



}

export const useNavigationStore = () => {
    return useStore().navigationStore;
};
export const getNavigationStore = () => {
    return getStore().navigationStore;
};
